import * as React from "react"
import {
  Segment,
  Header,
  Divider,
  Icon,
  Input,
  Button,
  Label,
  Modal,
  Message,
} from "semantic-ui-react"
import CheckboxTree from "react-checkbox-tree"
import { ITreeNode, ITreeState } from "../../../utils/tree/interfaces"
import { stringify, stringifyTree } from "../../../utils/stringifyier"
import {
  caracMatcher,
  defaultMatcher,
  expandFilteredNodes,
  multiFilterTree,
  searchNodeByCode,
  searchNodeByValue,
} from "../../../utils"
import Loading from "../com_loading"
import { IFieldState } from "../../product-form/_reducers/_utils/product-form-types"
import ButtonLock from "../buttons/com_button-lock"
import ButtonChange from "../buttons/com_button-change"
import _ from "lodash"
import { PreviousValueMessage } from "../form-field-generator/form-field-tools"
import { FieldType, IHandleChangeProps, IOldValue } from "../../../models"

export interface ITreeProps {
  caracFilter?: string
  classNames?: string
  propNodes: ITreeNode[]
  title: string
  disabled?: boolean
  disabledMessage?: React.ReactNode
  selected: IFieldState<ITreeNode>
  previousSelected?: IOldValue
  isNewProduct: boolean
  lockable: boolean
  nodesLoadingState: boolean
  purpleButton?: boolean
  tabIndex: number
  treeState: IFieldState<ITreeState>
  onChange: ({ code, fieldType, lock, fieldValue }: IHandleChangeProps) => void
  onResetCheckedNodes?: () => void
  onLockClick: (event: any, fieldType: FieldType) => void
  onModifyClick?: (event: any, fieldType: FieldType) => void
  allowParentCheck?: boolean
  translations: any
  type: "com" | "tech"
  displayed?: boolean
  headless?: boolean
}

const Tree = ({
  caracFilter,
  classNames = "",
  propNodes,
  lockable,
  selected,
  isNewProduct,
  previousSelected,
  disabled = false,
  disabledMessage = "",
  title,
  onChange,
  purpleButton,
  nodesLoadingState,
  onLockClick,
  onModifyClick,
  treeState,
  onResetCheckedNodes,
  allowParentCheck = false,
  translations,
  tabIndex,
  type,
  displayed = true,
  headless = false,
}: ITreeProps) => {
  const { checked = [], expanded = [] } = treeState.value
  const [open, setOpen] = React.useState(false)
  const [searchText, setSearchText] = React.useState<string>("")
  const [searchExpanded, setSearchExpanded] = React.useState<string[]>([])
  const [nodesToRender, setNodesToRender] = React.useState<ITreeNode[]>([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (searchText === "" || searchText.length > 2) {
      search()
    }
  }, [stringify(searchText), caracFilter])

  React.useEffect(() => {
    search()
  }, [stringifyTree(propNodes)])

  const setChecked = (nodeId: string | undefined, isChecked: boolean) => {
    const fieldValue = {
      ...treeState.value,
      checked: isChecked ? [nodeId] : [],
    }
    onChange({
      code: treeState.code,
      fieldType: treeState.fieldType,
      lock: treeState.lock,
      fieldValue,
    })
  }

  const setExpanded = (exp: string[]) => {
    onChange({
      code: treeState.code,
      fieldType: treeState.fieldType,
      lock: treeState.lock,
      fieldValue: { ...treeState.value, expanded: exp },
    })
  }

  const onSearchTextChange = (event: any, payload: any) => {
    setLoading(true)
    debouncedSearch(event, payload)
  }
  const debouncedSearch = _.debounce((event: any, { value = "" }: any) => {
    if (value !== searchText) {
      setSearchText(value)
    }
  }, 750)

  const handleOpen = () => setOpen(true)
  const handleClose =
    (save = false) =>
    () => {
      if (save && checked[0]) {
        const node = searchNodeByValue(propNodes, checked[0])
        if (node) {
          const { children, ...nodeWithoutChildren } = node
          onChange({
            code: selected.code,
            fieldType: selected.fieldType,
            lock: selected.lock,
            fieldValue: nodeWithoutChildren,
          })
        }
      }
      setOpen(false)
    }

  const search = () => {
    setLoading(true)

    const matchers = caracFilter
      ? [defaultMatcher, caracMatcher(caracFilter)]
      : [defaultMatcher]

    multiFilterTree({
      node: propNodes[0] || {},
      matchers: matchers,
      filter: searchText,
    }).then((res) => {
      setNodesToRender([res])
      if (searchText.length > 2) {
        expandFilteredNodes(res, searchText, setSearchExpanded)
      }
      setLoading(false)
    })
  }

  const onClickHandler = (node: ITreeNode) => {
    if (node.children === undefined) {
      const alreadyChecked = checked.some((c) => c === node.value)
      setChecked(node.value, !alreadyChecked)
    }
  }

  const handleCheck = (nodes: string[], node: ITreeNode) => {
    const isChecked = nodes.length > checked.length
    const id = isChecked
      ? nodes.find((n) => checked.every((c) => c !== n))
      : checked.find((c) => nodes.every((n) => n !== c))
    setChecked(id, isChecked)
  }

  const handleExpand = (actual: string[], node: ITreeNode) => {
    if (!node) {
      return setExpanded([...actual])
    }
    let newExpanded
    const nodeId = node.value

    if (expanded.find((e: string) => e === nodeId)) {
      newExpanded = expanded.filter((e: string) => e !== nodeId)
    } else {
      newExpanded = [...expanded, nodeId]
    }

    return setExpanded(newExpanded)
  }

  const getNodes = () => {
    return nodesToRender?.[0]?.children || []
  }

  const treeComponent = React.useMemo(() => {
    const nodes = getNodes()
    const display = nodes.length > 0
    return display ? (
      // @ts-ignore
      <CheckboxTree
        nodes={nodes}
        checked={checked}
        expanded={[
          ...expanded,
          ...(searchText.length < 2 ? [] : searchExpanded),
        ]}
        onCheck={handleCheck}
        expandOnClick={true}
        onClick={onClickHandler}
        // @ts-ignore
        onExpand={handleExpand}
        icons={{
          check: <Icon color="black" name="check square outline" />,
          uncheck: <Icon color="black" name="square outline" />,
          halfCheck: <Icon color="black" name="square" />,
          parentClose: <Icon color="black" name="folder outline" />,
          parentOpen: <Icon color="black" name="folder open outline" />,
          leaf: "",
          // leaf: <Icon color="black" name="file outline" />,
          expandClose: <Icon color="black" name="plus" />,
          expandOpen: <Icon color="black" name="minus" />,
          expandAll: (
            <Icon color="black" name="search plus" title="Tout ouvrir" />
          ),
          collapseAll: (
            <Icon color="black" name="search minus" title="Tout fermer" />
          ),
        }}
        noCascade={true}
        onlyLeafCheckboxes={!allowParentCheck}
        showExpandAll={true}
      />
    ) : (
      <Header style={{ margin: "auto" }}>
        {nodesLoadingState || "Aucun résultat"}
      </Header>
    )
  }, [
    stringifyTree(nodesToRender),
    stringify(checked),
    setChecked,
    // setChecked,
    stringify([...expanded, ...(searchText.length < 2 ? [] : searchExpanded)]),
  ])

  const previousSelectedNode = React.useMemo(() => {
    return searchNodeByCode(getNodes(), previousSelected?.value + "")
  }, [previousSelected, propNodes])

  if (!displayed) {
    return <section />
  }

  const color = purpleButton ? "violet" : "black"
  const basic = selected.lock === "unlock"
  // || this.props.formId === permanentFormIds.MULTI_MODIF

  return (
    <div className={"tree " + classNames}>
      {!headless && <h2 className="tree-label">{title}</h2>}
      <div
        className={
          "lock-div tree-button-div " +
          (selected.errorText ? "error-field " : "") +
          (headless ? "headless " : "")
        }
      >
        {previousSelectedNode &&
        previousSelected &&
        previousSelected.value !== selected.value.code ? (
          <PreviousValueMessage
            label={previousSelected.formattedDate}
            fitContent
            content={previousSelectedNode?.path}
          />
        ) : (
          ""
        )}
        {selected.errorText ? (
          <Message
            className="error-field-message"
            negative
            header={selected.errorText}
          />
        ) : (
          ""
        )}
        <div className="tree-modal-container">
          <Modal
            className="tree-popover"
            disabled={disabled}
            open={open}
            trigger={
              <Button
                type="button"
                disabled={disabled}
                tabIndex={tabIndex}
                className="element button tree-button"
                color={color}
                onClick={handleOpen}
                basic={basic}
              >
                <Icon name="tag" />
                {selected.value.path ||
                  (type === "com"
                    ? translations.selectComTreeLabel
                    : translations.selectTechTreeLabel)}
                {disabled && (
                  <div className="node-selection-disabled-message">
                    {disabledMessage}
                  </div>
                )}
              </Button>
            }
            on="click"
            onClose={handleClose(false)}
            style={{ width: "fit-content" }}
          >
            <Modal.Header>
              {selected.value.path ||
                (type === "com"
                  ? translations.selectComTreeLabel
                  : translations.selectTechTreeLabel)}
            </Modal.Header>
            <Modal.Content>
              <div className="tree-popover-content">
                <Input
                  icon="search"
                  placeholder="Recherchez un noeud..."
                  loading={loading}
                  onChange={onSearchTextChange}
                  autoFocus={true}
                />
                <Divider />
                <div className="tree tree-div">
                  {(nodesLoadingState || loading) && <Loading />}
                  {treeComponent}
                  <div className="tree-buttons">
                    {onResetCheckedNodes && (
                      <button
                        className="rct-option"
                        title="Désélectionner tout"
                        onClick={onResetCheckedNodes}
                      >
                        <Icon color="black" name="eraser" />
                      </button>
                    )}
                  </div>
                </div>
                <Button
                  style={{ margin: "auto !important" }}
                  className="colored-button"
                  onClick={handleClose(true)}
                  content="Valider"
                />
              </div>
            </Modal.Content>
          </Modal>
          {onModifyClick ? (
            <ButtonChange
              modified={selected.modified}
              onClick={onModifyClick}
              code={selected.code}
              fieldType={selected.fieldType}
            />
          ) : (
            <ButtonLock
              hidden={!isNewProduct}
              disabled={disabled}
              lockable={lockable}
              onClick={onLockClick}
              code={selected.code}
              lock={selected.lock}
              fieldType={selected.fieldType}
            />
          )}
        </div>
      </div>
    </div>
  )
}
// }

export default Tree
